import { useNavigate, useParams } from "@tanstack/solid-router";
import { createInfiniteQuery, createQuery, keepPreviousData } from "@tanstack/solid-query";
import { For, Show, createEffect, createMemo, createSignal } from "solid-js";
import { Avatar } from "~/components/Avatar";
import { SkillChips } from "~/components/SkillChips";
import { EventPageHeader } from "~/components/EventPageHeader";
import { OpenToBadges } from "~/components/OpenToBadges";
import { TicketClaimGate } from "~/components/TicketClaimGate";
import { companyPath } from "~/lib/company-path";
import {
  fetchConnectEvents,
  fetchDirectory,
  fetchInterests,
  everClaimedTicket,
  fetchProfile,
  type DirectoryCard,
} from "~/lib/api";
import { track } from "~/lib/analytics";
import { createDebouncedValue } from "~/lib/debounce";
import { eventDisplayTitle } from "~/lib/events";
import { OPEN_TO_KEYS, OPEN_TO_LABELS, type OpenToKey } from "~/lib/open-to";
import { profileQueryKey } from "~/lib/routes";

function SearchIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle cx="11" cy="11" r="7" stroke="currentColor" stroke-width="2" />
      <path d="M20 20L16 16" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
    </svg>
  );
}

function ChevronIcon() {
  return (
    <svg
      class="directory-card-chevron"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M9 6L15 12L9 18"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
        stroke-linejoin="round"
      />
    </svg>
  );
}

export function DirectoryPage() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const eventId = () => params().eventId ?? "";

  const [search, setSearch] = createSignal("");
  const [company, setCompany] = createSignal("");
  const [jobRole, setJobRole] = createSignal("");
  const [filterInterests, setFilterInterests] = createSignal<string[]>([]);
  const [filterOpenTo, setFilterOpenTo] = createSignal<OpenToKey[]>([]);
  const [filtersOpen, setFiltersOpen] = createSignal(false);

  // Debounce text filters so mobile keystrokes don't thrash the list / keyboard.
  const debouncedSearch = createDebouncedValue(search, 350);
  const debouncedCompany = createDebouncedValue(company, 350);
  const debouncedJobRole = createDebouncedValue(jobRole, 350);

  const events = createQuery(() => ({ queryKey: ["events"], queryFn: fetchConnectEvents }));
  const interestsQuery = createQuery(() => ({ queryKey: ["interests"], queryFn: fetchInterests }));
  const profileQuery = createQuery(() => ({
    queryKey: profileQueryKey(eventId()),
    queryFn: () => fetchProfile(eventId() || undefined),
  }));

  const needsTicket = () =>
    profileQuery.isSuccess && !everClaimedTicket(profileQuery.data);

  const filters = createMemo(() => ({
    eventId: eventId(),
    q: debouncedSearch().trim(),
    company: debouncedCompany().trim(),
    job_role: debouncedJobRole().trim(),
    interests: filterInterests(),
    open_to: filterOpenTo(),
  }));

  createEffect(() => {
    const f = filters();
    const hasFilters =
      !!f.q || !!f.company || !!f.job_role || f.interests.length > 0 || f.open_to.length > 0;
    if (!hasFilters) return;
    track(
      "directory_filter_changed",
      {
        has_query: !!f.q,
        company_set: !!f.company,
        job_role_set: !!f.job_role,
        interest_count: f.interests.length,
        open_to_count: f.open_to.length,
      },
      { ticketingEventId: f.eventId || undefined },
    );
  });

  const directoryQuery = createInfiniteQuery(() => ({
    queryKey: ["directory", filters()],
    queryFn: ({ pageParam }) =>
      fetchDirectory({
        ...filters(),
        cursor: pageParam as string | undefined,
        limit: 20,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor || undefined,
    placeholderData: keepPreviousData,
    enabled: everClaimedTicket(profileQuery.data),
  }));

  const searching = () =>
    search() !== debouncedSearch() ||
    company() !== debouncedCompany() ||
    jobRole() !== debouncedJobRole() ||
    (directoryQuery.isFetching && !directoryQuery.isFetchingNextPage);

  const selectedEvent = () =>
    events.data?.data.find((e) => e.ticketing_event_id === eventId());

  const eventTitle = () =>
    eventId() ? eventDisplayTitle(selectedEvent(), eventId()) : "All cities";

  const connectOpen = () => selectedEvent()?.connect_open !== false;

  const allCards = createMemo(() => {
    const pages = directoryQuery.data?.pages ?? [];
    return pages.flatMap((p) => p.data);
  });

  function toggleFilterInterest(id: string) {
    setFilterInterests((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  function toggleFilterOpenTo(key: OpenToKey) {
    setFilterOpenTo((prev) => (prev.includes(key) ? prev.filter((x) => x !== key) : [...prev, key]));
  }

  function openProfile(card: DirectoryCard) {
    if (eventId()) {
      navigate({
        to: "/directory/$eventId/profile/$profileId",
        params: { eventId: eventId(), profileId: card.id },
      });
      return;
    }
    navigate({ to: "/profile/$profileId", params: { profileId: card.id } });
  }

  return (
    <section class="page page--flush directory-page">
      <Show when={needsTicket()}>
        <div style={{ padding: "20px" }}>
          <TicketClaimGate
            eventId={eventId() || undefined}
            cityLabel={eventTitle()}
            connectOpen={eventId() ? connectOpen() : undefined}
          />
        </div>
      </Show>

      <Show when={!needsTicket()}>
      <EventPageHeader eventTitle={eventTitle()} label="Directory" />

      <label class="search-bar">
        <SearchIcon />
        <input
          type="search"
          enterkeyhint="search"
          autocomplete="off"
          autocorrect="off"
          autocapitalize="off"
          spellcheck={false}
          placeholder="Search practitioners…"
          value={search()}
          onInput={(e) => setSearch(e.currentTarget.value)}
        />
        <Show when={searching()}>
          <span class="search-bar-status" aria-live="polite">
            Searching…
          </span>
        </Show>
      </label>

      <div class="filter-chips">
        <button
          type="button"
          class={`btn btn-outline btn-sm${filtersOpen() ? "" : ""}`}
          onClick={() => setFiltersOpen((v) => !v)}
        >
          {filtersOpen() ? "Hide filters" : "More filters"}
        </button>
        <For each={OPEN_TO_KEYS}>
          {(key) => (
            <button
              type="button"
              class={`interest-chip${filterOpenTo().includes(key) ? " selected" : ""}`}
              onClick={() => toggleFilterOpenTo(key)}
            >
              {OPEN_TO_LABELS[key]}
            </button>
          )}
        </For>
        <Show when={interestsQuery.data} keyed>
          {(payload) => (
            <For each={payload.data.slice(0, 6)}>
              {(tag) => (
                <button
                  type="button"
                  class={`interest-chip${filterInterests().includes(tag.id) ? " selected" : ""}`}
                  onClick={() => toggleFilterInterest(tag.id)}
                >
                  {tag.label}
                </button>
              )}
            </For>
          )}
        </Show>
      </div>

      <Show when={filtersOpen()}>
        <div class="directory-filters directory-filters--compact">
          <label class="field">
            <span>Company</span>
            <input
              value={company()}
              autocomplete="organization"
              onInput={(e) => setCompany(e.currentTarget.value)}
            />
          </label>
          <label class="field">
            <span>Role</span>
            <input
              value={jobRole()}
              autocomplete="organization-title"
              onInput={(e) => setJobRole(e.currentTarget.value)}
            />
          </label>
        </div>
      </Show>

      <Show when={directoryQuery.isPending && !directoryQuery.data}>
        <p class="muted" style={{ padding: "0 20px" }}>
          Loading attendees…
        </p>
      </Show>
      <Show when={directoryQuery.isError}>
        <div class="error-box" style={{ margin: "0 20px" }}>
          {(directoryQuery.error as Error).message}
        </div>
      </Show>

      <Show when={!directoryQuery.isPending && allCards().length === 0 && !searching()}>
        <div class="empty-state" style={{ margin: "16px 20px" }}>
          <p>No one in the directory yet — or no matches for your filters.</p>
          <p class="muted">Invite peers to opt in so the directory feels alive before doors open.</p>
        </div>
      </Show>

      <ul class={`directory-grid${searching() ? " directory-grid--searching" : ""}`}>
        <For each={allCards()}>
          {(card) => (
            <li>
              <button type="button" class="directory-card" onClick={() => openProfile(card)}>
                <Avatar url={card.avatar_url} name={card.display_name} size="md" />
                <div class="directory-card-body">
                  <strong>{card.display_name ?? "Attendee"}</strong>
                  <span>{card.headline ?? card.job_role ?? "Conference attendee"}</span>
                  <Show when={card.company} fallback={<span class="muted" />}>
                    <a
                      class="muted"
                      href={companyPath(card.company, card.company_slug) ?? "/companies"}
                      onClick={(e) => e.stopPropagation()}
                    >
                      {card.company}
                    </a>
                  </Show>
                  <Show when={card.pinned_project}>
                    <span class="directory-card-project">{card.pinned_project!.title}</span>
                  </Show>
                  <SkillChips skills={card.skills} limit={3} />
                  <OpenToBadges flags={card} />
                  <Show when={card.interests?.length}>
                    <div class="directory-card-tags">
                      <For each={card.interests.slice(0, 3)}>
                        {(tag) => <span class="track-badge">{tag.label}</span>}
                      </For>
                    </div>
                  </Show>
                </div>
                <ChevronIcon />
              </button>
            </li>
          )}
        </For>
      </ul>

      <Show when={directoryQuery.hasNextPage}>
        <div class="wizard-actions" style={{ padding: "0 20px 16px" }}>
          <button
            type="button"
            class="btn btn-outline"
            disabled={directoryQuery.isFetchingNextPage}
            onClick={() => directoryQuery.fetchNextPage()}
          >
            {directoryQuery.isFetchingNextPage ? "Loading…" : "Load more"}
          </button>
        </div>
      </Show>
      </Show>
    </section>
  );
}
